import GiftBoxApi from '@apis/gift/GiftBoxApi';
import giftBoxQueries from '@apis/queries/giftBoxQueries';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { isValidGiftCode, normalizeGiftCode } from './giftCode';

function useRegisterGift(onSuccess?: () => void, onError?: (message: string) => void) {
    const projectId = useProjectId();
    const { t } = useTranslation('gift');
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: (couponNumber: string) => GiftBoxApi.registerExternalGift({ projectId, couponNumber }),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: giftBoxQueries._def });
            toast.success(t('newGift.success'));
            onSuccess?.();
        },
        onError(error: AxiosError<ErrorResponse>) {
            const messageKeys: Record<string, string> = {
                GIFT_COUPON_ALREADY_USED: 'newGift.alreadyUsed',
                DUPLICATE_GIFT_COUPON: 'newGift.alreadyRegistered',
                EXPIRED_COUPON: 'newGift.expiredCode',
            };
            const message = t(messageKeys[error.response?.data?.code ?? ''] ?? 'newGift.invalidCode');
            if (onError) onError(message);
            else toast.error(message);
        },
    });
    const register = (value: string) => {
        const code = normalizeGiftCode(value);
        if (isPending) return;
        if (!isValidGiftCode(code)) {
            const message = t('newGift.invalidCode');
            if (onError) onError(message);
            else toast.error(message);
            return;
        }
        mutate(code);
    };
    return { register, isPending };
}

export default useRegisterGift;
